import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { api } from '../../api/api';
import { getStores } from '../../Redux/Actions/storeAction';
import toast from 'react-hot-toast';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  Box, Typography, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Grid,
  CircularProgress, Select, MenuItem, FormControl, InputLabel,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import './invoiceDashboard.css';

const STATUS_COLOR = { paid: '#22c55e', pending: '#f59e0b', overdue: '#ef4444', cancelled: '#9ca3af' };
const STATUS_BG = { paid: '#f0fdf4', pending: '#fffbeb', overdue: '#fef2f2', cancelled: '#f3f4f6' };

function InvoiceDashboard({ user }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { stores } = useSelector(s => s.Store);

  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterStore, setFilterStore] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [auditDialog, setAuditDialog] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const PAGE_SIZE = 20;

  useEffect(() => {
    dispatch(getStores(user._id));
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sumRes, listRes] = await Promise.all([
        api.get(`/payment/summary/${user._id}`),
        api.get(`/payment/list/${user._id}`),
      ]);
      setSummary(sumRes.data);
      setInvoices(listRes.data.payments || []);
    } catch (e) {
      toast.error('Failed to load invoice data');
    }
    setLoading(false);
  };

  const openAudit = async (storeId) => {
    setAuditDialog(storeId);
    setAuditLoading(true);
    try {
      const res = await api.get(`/payment/audit/store/${storeId}?limit=50`);
      setAuditLogs(res.data.logs || []);
    } catch (e) {
      toast.error('Failed to load audit logs');
    }
    setAuditLoading(false);
  };

  const filtered = invoices.filter(p => {
    if (filterStatus !== 'all' && p.status !== filterStatus) return false;
    if (filterStore !== 'all' && p.store?._id !== filterStore) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(p.invoiceNumber?.toLowerCase().includes(q) ||
            p.store?.name?.toLowerCase().includes(q) ||
            p.memo?.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  const fmt = (n) => `$${(n || 0).toLocaleString()}`;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

  const statusCounts = {
    all: invoices.length,
    pending: invoices.filter(p => p.status === 'pending').length,
    paid: invoices.filter(p => p.status === 'paid').length,
    overdue: invoices.filter(p => p.status === 'overdue').length,
  };

  const exportCsv = () => {
    const headers = ['Invoice #', 'Store', 'Type', 'Amount', 'Status', 'Due Date', 'Paid Date'];
    const rows = filtered.map(p => [
      p.invoiceNumber, p.store?.name || '', p.type, p.amount, p.status,
      fmtDate(p.dueDate), fmtDate(p.paidDate),
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoices_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Store billing summary
  const storeBilling = stores.map(s => {
    const storeInvoices = invoices.filter(p => p.store?._id === s._id);
    const totalPaid = storeInvoices.filter(p => p.status === 'paid').reduce((acc, p) => acc + (p.amount || 0), 0);
    const totalPending = storeInvoices.filter(p => p.status === 'pending').reduce((acc, p) => acc + (p.amount || 0), 0);
    const totalOverdue = storeInvoices.filter(p => p.status === 'overdue').reduce((acc, p) => acc + (p.amount || 0), 0);
    return { ...s, totalPaid, totalPending, totalOverdue, invoiceCount: storeInvoices.length };
  }).filter(s => s.invoiceCount > 0).sort((a, b) => b.totalOverdue - a.totalOverdue || b.totalPending - a.totalPending);

  if (loading) {
    return (
      <Layout title="Invoice Dashboard" user={user}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      </Layout>
    );
  }

  return (
    <Layout title="Invoice Dashboard" user={user}>
      <Box className="inv-root">
        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { icon: <ReceiptLongIcon />, label: 'Total Invoices', value: summary?.totalCount || invoices.length, color: '#551FFF', bg: '#f8f7ff' },
            { icon: <AttachMoneyIcon />, label: 'Total Revenue', value: fmt(summary?.totalRevenue || invoices.filter(p => p.status === 'paid').reduce((a, p) => a + (p.amount || 0), 0)), color: '#22c55e', bg: '#f0fdf4' },
            { icon: <PendingActionsIcon />, label: 'Pending Amount', value: fmt(summary?.pendingAmount || invoices.filter(p => p.status === 'pending').reduce((a, p) => a + (p.amount || 0), 0)), color: '#f59e0b', bg: '#fffbeb' },
            { icon: <WarningAmberIcon />, label: 'Overdue Amount', value: fmt(summary?.overdueAmount || invoices.filter(p => p.status === 'overdue').reduce((a, p) => a + (p.amount || 0), 0)), color: '#ef4444', bg: '#fef2f2' },
          ].map(({ icon, label, value, color, bg }) => (
            <Grid item xs={6} md={3} key={label}>
              <Paper sx={{ p: 2, borderRadius: 3, borderTop: `4px solid ${color}`, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ background: bg, color, borderRadius: 2, p: 1.5, display: 'flex' }}>{icon}</Box>
                <Box>
                  <Typography sx={{ fontSize: 11, color: '#92959E', fontWeight: 600 }}>{label}</Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color }}>{value}</Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Store Billing Summary */}
        <Paper sx={{ p: 2.5, borderRadius: 3, mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StorefrontIcon sx={{ color: '#551FFF', fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Store Billing Summary</Typography>
            </Box>
            <Chip label={`${storeBilling.length} stores`} size="small" />
          </Box>
          <TableContainer sx={{ maxHeight: 280 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Store</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Invoices</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#22c55e' }}>Paid</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#f59e0b' }}>Pending</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#ef4444' }}>Overdue</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {storeBilling.map(s => (
                  <TableRow key={s._id} hover>
                    <TableCell>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{s.name}</Typography>
                      <Typography sx={{ fontSize: 11, color: '#aaa' }}>{s.town}, {s.state}</Typography>
                    </TableCell>
                    <TableCell align="right">{s.invoiceCount}</TableCell>
                    <TableCell align="right" sx={{ color: '#22c55e', fontWeight: 600 }}>{fmt(s.totalPaid)}</TableCell>
                    <TableCell align="right" sx={{ color: '#f59e0b', fontWeight: 600 }}>{fmt(s.totalPending)}</TableCell>
                    <TableCell align="right" sx={{ color: s.totalOverdue > 0 ? '#ef4444' : '#aaa', fontWeight: 600 }}>{fmt(s.totalOverdue)}</TableCell>
                    <TableCell align="center">
                      <Button size="small" sx={{ fontSize: 11, minWidth: 0 }} onClick={() => { setFilterStore(s._id); setPage(0); }}>Filter</Button>
                      <Button size="small" sx={{ fontSize: 11, minWidth: 0 }} onClick={() => openAudit(s._id)}>Audit</Button>
                      <Button size="small" sx={{ fontSize: 11, minWidth: 0 }} onClick={() => navigate(`/pricing/${s._id}`)}>Prices</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Invoice List */}
        <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15 }}>All Invoices</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Search..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(0); }}
                InputProps={{ startAdornment: <SearchIcon sx={{ color: '#aaa', mr: 0.5, fontSize: 18 }} /> }}
                sx={{ width: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Store</InputLabel>
                <Select value={filterStore} label="Store" onChange={e => { setFilterStore(e.target.value); setPage(0); }}>
                  <MenuItem value="all">All Stores</MenuItem>
                  {stores.map(s => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {['all', 'pending', 'paid', 'overdue'].map(s => (
                  <Chip
                    key={s}
                    label={`${s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)} (${statusCounts[s] || 0})`}
                    size="small"
                    onClick={() => { setFilterStatus(s); setPage(0); }}
                    sx={{
                      cursor: 'pointer',
                      fontWeight: filterStatus === s ? 700 : 400,
                      background: filterStatus === s ? (STATUS_BG[s] || '#f0f0f0') : undefined,
                      border: filterStatus === s ? `1px solid ${STATUS_COLOR[s] || '#551FFF'}` : undefined,
                    }}
                  />
                ))}
              </Box>
              <Button size="small" startIcon={<FileDownloadIcon />} onClick={exportCsv}>CSV</Button>
            </Box>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Invoice #</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Store</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Paid Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Memo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#aaa' }}>No invoices found</TableCell>
                  </TableRow>
                ) : paged.map(p => (
                  <TableRow key={p._id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{p.invoiceNumber || '-'}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{p.store?.name || '-'}</Typography>
                      <Typography sx={{ fontSize: 11, color: '#aaa' }}>{p.store?.town}, {p.store?.state}</Typography>
                    </TableCell>
                    <TableCell sx={{ textTransform: 'capitalize', fontSize: 12 }}>{p.type}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>${p.amount}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{fmtDate(p.dueDate)}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{fmtDate(p.paidDate)}</TableCell>
                    <TableCell>
                      <Chip
                        label={p.status}
                        size="small"
                        sx={{
                          background: STATUS_BG[p.status],
                          color: STATUS_COLOR[p.status],
                          border: `1px solid ${STATUS_COLOR[p.status]}33`,
                          fontWeight: 600,
                          fontSize: 11,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: '#888', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.memo || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
              <Button size="small" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</Button>
              <Typography sx={{ display: 'flex', alignItems: 'center', fontSize: 13 }}>
                {page + 1} / {totalPages}
              </Typography>
              <Button size="small" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</Button>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Audit Log Dialog */}
      <Dialog open={!!auditDialog} onClose={() => setAuditDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Payment Audit Log</DialogTitle>
        <DialogContent>
          {auditLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : auditLogs.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No audit records</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Memo</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLogs.map((log, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ fontSize: 12 }}>{fmtDate(log.createdAt)}</TableCell>
                    <TableCell>
                      <Chip label={log.action} size="small" sx={{ fontSize: 10, height: 20 }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{log.actorLabel || log.actor?.username || '-'}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{log.memo || '-'}</TableCell>
                    <TableCell sx={{ fontSize: 11, color: '#888', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.diff ? JSON.stringify(log.diff) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default InvoiceDashboard;
